import type { IHttpClient } from '../api/client';
import { AppError } from '../api/errors';

import {
  mapV2Output,
  toV2OrchestratorSettings,
  V2_WORKER_API_VERSION,
  type V2OrchestratorSettings,
  type WireV2Output,
} from './judgeV2Mapper';
import {
  mapValidationRun,
  type WireValidationRun,
} from './judgeValidationMapper';
import type {
  SyntheticRunConfig,
  SyntheticRunCreateResult,
  SyntheticRunDetail,
  SyntheticJobStatus,
} from './syntheticJob.interface';
import type {
  CreateGraderRequest,
  CreateJudgeResponse,
  EvaluationRun,
  GraderRun,
  GraderVersion,
  IWorkflowApiService,
  JudgeCreationJobResult,
  JudgeOrchestratorSettings,
  JudgeValidationRun,
  JudgeVariant,
  ListEvaluationRunsResponse,
  ServerGrader,
  UpdateGraderRequest,
  UpsertGraderRequest,
  ValidateJudgeRequest,
  ValidateJudgeResponse,
} from './workflowApi.interface';

const DEFAULT_WORKSPACE = 'public';

const ROUTES = {
  // Graders are workspace-scoped on the server: see GradersController.cs at
  // `Route("workspaces/{workspaceName}/evaluation/graders")`. The caller must
  // supply the active workspace — there is no default. Pages render a
  // "pick a workspace" placeholder when none is selected, mirroring the
  // eval-templates flow.
  graders: (ws: string) =>
    `workspaces/${encodeURIComponent(ws)}/evaluation/graders`,
  grader: (ws: string, name: string) =>
    `workspaces/${encodeURIComponent(ws)}/evaluation/graders/${encodeURIComponent(name)}`,
  graderVersions: (ws: string, name: string) =>
    `workspaces/${encodeURIComponent(ws)}/evaluation/graders/${encodeURIComponent(name)}/versions`,
  createJudge: 'grader-runs:create-judge',
  validateJudge: 'grader-runs:validate-judge',
  graderRuns: 'grader-runs',
  graderRun: (runId: string) => `grader-runs/${runId}`,
  validationRun: (runId: string) => {
    const query = new URLSearchParams({ type: 'judge-validation' });
    return `grader-runs/${encodeURIComponent(runId)}?${query.toString()}`;
  },
  syntheticRuns: (ws: string) => `workspaces/${ws}/data-gen/synthetic-runs`,
  syntheticRun: (ws: string, runId: string) =>
    `workspaces/${ws}/data-gen/synthetic-runs/${runId}`,
  syntheticRunBlob: (ws: string, runId: string, blobName: string) =>
    `workspaces/${ws}/data-gen/synthetic-runs/${runId}/blobs/${encodeURIComponent(blobName)}`,
  evaluationRuns: (ws: string) =>
    `workspaces/${encodeURIComponent(ws)}/evaluation/evaluation-runs`,
  evaluationRun: (ws: string, id: string) =>
    `workspaces/${encodeURIComponent(ws)}/evaluation/evaluation-runs/${encodeURIComponent(id)}`,
  evaluationRunResult: (ws: string, id: string) =>
    `workspaces/${encodeURIComponent(ws)}/evaluation/evaluation-runs/${encodeURIComponent(id)}/result`,
  evaluationRunReport: (ws: string, runIds: string[]) => {
    const params = new URLSearchParams(runIds.map((id) => ['ids', id]));
    return `workspaces/${encodeURIComponent(ws)}/evaluation/evaluation-runs/report?${params.toString()}`;
  },
};

// Server returns graders as an OData-style envelope (`ListGradersResponse`).
interface ListGradersWireResponse {
  value: ServerGrader[];
  nextLink?: string | null;
}

// Server returns grader versions as an OData-style envelope
// (`ListGraderVersionsResponse`): `{ value: GraderVersion[], nextLink?: string }`.
interface ListGraderVersionsWireResponse {
  value: GraderVersion[];
  nextLink?: string;
}

/** Wire shape returned by the synthetic runs API. */
interface SyntheticRunWireResponse {
  id: string;
  workspaceName: string;
  displayName?: string;
  generationType: string;
  runState: string;
  config: SyntheticRunConfig;
  result?: {
    messagesGenerated: number;
    generationTimeSeconds: number;
    blobs: string[];
  };
  createdAt: string;
  completedAt?: string;
  error?: string;
}

function mapWireToSyntheticRunDetail(
  wire: SyntheticRunWireResponse,
): SyntheticRunDetail {
  return {
    id: wire.id,
    workspaceName: wire.workspaceName,
    generationType: wire.generationType,
    runState: wire.runState as SyntheticJobStatus,
    config: wire.config,
    result: wire.result,
    createdAt: wire.createdAt,
    completedAt: wire.completedAt,
    error: wire.error,
  };
}

interface WireJudgePyVariant {
  technique: string;
  name: string;
  // Newer payloads nest the prompt under `judgeOutput.systemPrompt`. Older
  // persisted runs (pre WorkflowSvc PR 1572357 "Add missing primitives to
  // judge variant output") carry a flattened top-level `prompt` instead.
  judgeOutput?: {
    systemPrompt?: string;
    userTemplate?: string;
  };
  prompt?: string;
  // Same story for kappa: newer payloads use `cohensKappa`, pre-rename
  // persisted runs use `kappa`.
  cohensKappa?: number;
  kappa?: number;
  accuracy: number;
  oneOffAccuracy?: number;
  weightedKappa?: number;
  krippendorffAlpha?: number;
  bias?: number;
  f1Score: number;
  precision: number;
  recall: number;
}

interface WireJudgeCreationResults {
  finalPrompt: string;
  finalAccuracy: number;
  finalKappa: number;
  iterations: number;
  trainSize: number;
  testSize: number;
  winningTechnique: string;
  variants?: WireJudgePyVariant[];
}

interface WireGraderRun {
  id: string;
  type: string;
  status: GraderRun['status'];
  graderName?: string;
  createdAt?: string;
  startedAt?: string;
  completedAt?: string;
  error?: string;
  // Polymorphic payload discriminated by the run's `type`. For
  // `judge-creation` runs, the shape is `WireJudgeCreationResults` (v1) or the
  // v2 `output` envelope; both share this slot.
  results?: WireJudgeCreationResults & WireV2JudgeCreationResults;
}

// A judge-creation run produced by the v2 pipeline carries a `workerApiVersion`
// tag and stores the raw ranked-candidates response under `results.output`
// instead of the v1 typed fields. Read both off the same wire envelope.
interface WireV2JudgeCreationResults {
  workerApiVersion?: string;
  output?: WireV2Output;
}

// `true` when the run's results came from the v2 pipeline (tagged
// `workerApiVersion === 'createJudgeV2'`, with the ranked candidates under
// `output`). v1 runs leave both unset.
function isV2Results(
  r: (WireJudgeCreationResults & WireV2JudgeCreationResults) | undefined,
): boolean {
  return (
    r?.workerApiVersion === V2_WORKER_API_VERSION || r?.output !== undefined
  );
}

function mapV2Results(r: WireV2JudgeCreationResults): JudgeCreationJobResult {
  const mapped = mapV2Output(r.output ?? {});
  return {
    variants: mapped.variants,
    finalPrompt: mapped.finalPrompt,
    finalAccuracy: mapped.finalAccuracy,
    finalKappa: mapped.finalKappa,
    winningTechnique: mapped.winningTechnique,
  };
}

// Build the `POST /grader-runs:create-judge` body. When the caller opts into v2
// (`useLegacy === false`), the orchestrator settings are translated to the v2
// wire shape the worker forwards as `v2Settings`; otherwise the v1 settings
// pass through unchanged. `useLegacy` is only sent when explicitly set so the
// backend default (true) is preserved. The server types `orchestratorSettings`
// as an opaque JSON blob, so either settings shape is accepted.
interface CreateGraderWireBody extends Omit<
  CreateGraderRequest,
  'orchestratorSettings'
> {
  orchestratorSettings?: JudgeOrchestratorSettings | V2OrchestratorSettings;
}

function toCreateGraderBody(
  payload: CreateGraderRequest,
): CreateGraderWireBody {
  if (payload.useLegacy !== false) return payload;
  return {
    ...payload,
    orchestratorSettings: payload.orchestratorSettings
      ? toV2OrchestratorSettings(payload.orchestratorSettings)
      : undefined,
  };
}

// Mirrors the wire shape emitted by WorkflowSvc's `JudgeCreationJobExecutor`.
// Current runs serialize `JudgePyVariant` (and transitively worker-py's
// `JudgeVariantModel`) verbatim with camelCase: nested `judgeOutput`,
// `cohensKappa`, and extended metrics at the top level. Pre-PR-1572357
// persisted runs in Cosmos used a flatter shape (`prompt`, `kappa`) without
// `judgeOutput`; we read both for backward compatibility. Extended metrics
// (`oneOffAccuracy`, `weightedKappa`, `krippendorffAlpha`, `bias`) fall back
// to the archive (raw worker-py payload), matched by variant name, with a
// final fallback to 0 if neither is present.
function mapVariant(v: WireJudgePyVariant): JudgeVariant {
  return {
    name: v.name,
    technique: v.technique,
    cohensKappa: v.cohensKappa ?? v.kappa ?? 0,
    accuracy: v.accuracy,
    oneOffAccuracy: v.oneOffAccuracy ?? 0,
    weightedKappa: v.weightedKappa ?? 0,
    krippendorffAlpha: v.krippendorffAlpha ?? 0,
    bias: v.bias ?? 0,
    f1Score: v.f1Score,
    precision: v.precision,
    recall: v.recall,
    judgeOutput: {
      systemPrompt: v.judgeOutput?.systemPrompt ?? v.prompt,
      userTemplate: v.judgeOutput?.userTemplate,
    },
  };
}

function mapResults(r: WireJudgeCreationResults): JudgeCreationJobResult {
  return {
    variants: r.variants?.map((v) => mapVariant(v)) ?? [],
    finalPrompt: r.finalPrompt,
    finalAccuracy: r.finalAccuracy,
    finalKappa: r.finalKappa,
    winningTechnique: r.winningTechnique,
    iterations: r.iterations,
    trainSize: r.trainSize,
    testSize: r.testSize,
  };
}

function mapRun(r: WireGraderRun): GraderRun {
  return {
    id: r.id,
    type: r.type,
    status: r.status,
    graderName: r.graderName,
    createdAt: r.createdAt,
    startedAt: r.startedAt,
    completedAt: r.completedAt,
    error: r.error,
    results: r.results
      ? isV2Results(r.results)
        ? mapV2Results(r.results)
        : mapResults(r.results)
      : undefined,
  };
}

export class WorkflowApiService implements IWorkflowApiService {
  constructor(private readonly client: IHttpClient) {}

  /** Fetch all graders for a workspace. */
  async listGraders(
    workspaceName: string,
    options?: { filter?: string },
  ): Promise<ServerGrader[]> {
    const route = ROUTES.graders(workspaceName);
    const initialUrl = options?.filter
      ? `${route}?$filter=${encodeURIComponent(options.filter)}`
      : route;
    const graders: ServerGrader[] = [];
    let url = initialUrl;

    while (true) {
      const { data } = await this.client.get<ListGradersWireResponse>(url);
      graders.push(...data.value);
      if (!data.nextLink) return graders;
      if (data.value.length === 0) {
        throw new AppError(
          'Grader listing returned an empty page with a next link.',
          'UNKNOWN',
        );
      }

      // The server's absolute nextLink may point to an internal proxy address.
      url = `${initialUrl}${initialUrl.includes('?') ? '&' : '?'}$skip=${graders.length}`;
    }
  }

  async getGrader(workspaceName: string, name: string): Promise<ServerGrader> {
    const { data } = await this.client.get<ServerGrader>(
      ROUTES.grader(workspaceName, name),
    );
    return data;
  }

  /** Freeze the grader working copy as the next immutable version. */
  async snapshotGrader(
    workspaceName: string,
    name: string,
  ): Promise<GraderVersion> {
    const { data } = await this.client.post<GraderVersion>(
      `${ROUTES.grader(workspaceName, name)}:snapshot`,
      undefined,
    );
    return data;
  }

  /**
   * List a grader's immutable version snapshots via
   * `GET workspaces/{ws}/evaluation/graders/{name}/versions` (ascending order).
   */
  async listGraderVersions(
    workspaceName: string,
    name: string,
  ): Promise<GraderVersion[]> {
    const { data } = await this.client.get<ListGraderVersionsWireResponse>(
      ROUTES.graderVersions(workspaceName, name),
    );
    return data.value;
  }

  /** List all grader-runs via `GET /grader-runs`. Filter client-side by `graderName`. */
  async listGraderRuns(): Promise<GraderRun[]> {
    const { data } = await this.client.get<WireGraderRun[]>(ROUTES.graderRuns);
    return data.map(mapRun);
  }

  /** Fetch a single grader-run (with inline results) via `GET /grader-runs/{id}`. */
  async getGraderRun(runId: string): Promise<GraderRun> {
    const { data } = await this.client.get<WireGraderRun>(
      ROUTES.graderRun(runId),
    );
    return mapRun(data);
  }

  /** Create a new judge-creation grader run via `POST /grader-runs:create-judge`. */
  async createGrader(
    payload: CreateGraderRequest,
  ): Promise<CreateJudgeResponse> {
    const { data } = await this.client.post<CreateJudgeResponse>(
      ROUTES.createJudge,
      toCreateGraderBody(payload),
    );
    return data;
  }

  /** Enqueue a judge-validation run via `POST /grader-runs:validate-judge`. */
  async validateJudge(
    payload: ValidateJudgeRequest,
  ): Promise<ValidateJudgeResponse> {
    const { data } = await this.client.post<ValidateJudgeResponse>(
      ROUTES.validateJudge,
      payload,
    );
    return data;
  }

  /** Fetch a single validation run via `GET /grader-runs/{id}?type=judge-validation`. */
  async getValidationRun(runId: string): Promise<JudgeValidationRun> {
    const { data } = await this.client.get<WireValidationRun>(
      ROUTES.validationRun(runId),
    );
    return mapValidationRun(data);
  }

  /** Update an existing grader via `PATCH workspaces/{ws}/evaluation/graders/{name}`. */
  async updateGrader(
    workspaceName: string,
    name: string,
    payload: UpdateGraderRequest,
  ): Promise<void> {
    await this.client.patch<void>(ROUTES.grader(workspaceName, name), payload);
  }

  /** Create or replace a grader directly via `PUT workspaces/{ws}/evaluation/graders/{name}`. */
  async upsertGrader(
    workspaceName: string,
    name: string,
    payload: UpsertGraderRequest,
  ): Promise<ServerGrader> {
    const { data } = await this.client.put<ServerGrader>(
      ROUTES.grader(workspaceName, name),
      payload,
    );
    return data;
  }

  /** Delete a grader via `DELETE workspaces/{ws}/evaluation/graders/{name}`. */
  async deleteGrader(workspaceName: string, name: string): Promise<void> {
    await this.client.delete<void>(ROUTES.grader(workspaceName, name));
  }

  // ── Synthetic runs ────────────────────────────────────────────────────

  /** Submit a new synthetic data generation run. Returns the run resource. */
  async createSyntheticRun(
    request: SyntheticRunConfig,
    workspace: string = DEFAULT_WORKSPACE,
  ): Promise<SyntheticRunCreateResult> {
    const body = {
      generationType: 'kgChannels',
      config: request,
    };
    const { data } = await this.client.post<SyntheticRunWireResponse>(
      ROUTES.syntheticRuns(workspace),
      body,
    );
    return { runId: data.id, status: data.runState };
  }

  /** Fetch a single synthetic run by id. */
  async getSyntheticRun(
    runId: string,
    workspace: string = DEFAULT_WORKSPACE,
  ): Promise<SyntheticRunDetail> {
    const { data } = await this.client.get<SyntheticRunWireResponse>(
      ROUTES.syntheticRun(workspace, runId),
    );
    return mapWireToSyntheticRunDetail(data);
  }

  /**
   * Fetch a result blob as plain text. The Workflow API streams the blob
   * through using its own credential.
   */
  async getSyntheticRunBlobText(
    runId: string,
    blobName: string,
    workspace: string = DEFAULT_WORKSPACE,
  ): Promise<string> {
    const { data } = await this.client.getText(
      ROUTES.syntheticRunBlob(workspace, runId, blobName),
    );
    return data;
  }

  /** Fetch the generated HTML report for one or multiple completed evaluation runs. */
  async getEvaluationReport(
    workspaceName: string,
    runIds: string[],
  ): Promise<string> {
    const { data } = await this.client.getText(
      ROUTES.evaluationRunReport(workspaceName, runIds),
    );
    return data;
  }

  /** Fetch a single evaluation run by ID. Caller chooses whether to expand the result. */
  async getEvaluationRun(
    workspaceName: string,
    id: string,
  ): Promise<EvaluationRun> {
    const { data } = await this.client.get<EvaluationRun>(
      ROUTES.evaluationRun(workspaceName, id),
    );
    return data;
  }

  /**
   * Fetch the raw result blob for an evaluation run. Returns the parsed
   * JSON when present (200), or `null` when the result is missing (404).
   * Other HTTP errors propagate so callers can surface a generic error.
   */
  async getEvaluationRunResult(
    workspaceName: string,
    id: string,
  ): Promise<unknown> {
    try {
      const { data } = await this.client.get<unknown>(
        ROUTES.evaluationRunResult(workspaceName, id),
      );
      return data;
    } catch (err: unknown) {
      if (err instanceof AppError && err.statusCode === 404) {
        return null;
      }
      throw err;
    }
  }

  /** List evaluation runs for a workspace. */
  async listEvaluationRuns(
    workspaceName: string,
    options: { top?: number; skip?: number; filter?: string } = {},
  ): Promise<ListEvaluationRunsResponse> {
    const params = new URLSearchParams();
    params.set('$orderby', 'systemProperties/createdDate desc');
    if (options.top !== undefined) params.set('$top', String(options.top));
    if (options.skip !== undefined) params.set('$skip', String(options.skip));
    if (options.filter) params.set('$filter', options.filter);
    const query = params.size > 0 ? `?${params.toString()}` : '';
    const { data } = await this.client.get<{
      value: EvaluationRun[];
      nextLink: string | null;
    }>(`${ROUTES.evaluationRuns(workspaceName)}${query}`);
    return data;
  }
}
